/**
 * /login
 *
 * Built rather than stubbed: the backend auth is complete, and without a
 * working sign-in there is no way to reach any of the shell routes to check
 * that they render. This is the one placeholder-phase page that has to be real.
 */

import { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const { login, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Where ProtectedRoute bounced them from, if it did.
  const from = location.state?.from ?? '/dashboard';

  if (!isLoading && isAuthenticated) return <Navigate to={from} replace />;

  async function onSubmit(event) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const result = await login(email, password, rememberMe);

    if (result.ok) {
      navigate(from, { replace: true });
    } else {
      // The backend answers identically for a missing account, a disabled one
      // and a wrong password. Passing its message straight through keeps it
      // that way rather than inventing a more specific one here.
      setError(result.error);
      setBusy(false);
    }
  }

  return (
    <div className="hd-auth">
      <div className="hd-auth-inner hd-stack">
        <div>
          <div
            className="hd-brand"
            style={{ padding: 0, fontSize: 22, marginBottom: 4 }}
          >
            HOPDARTS
          </div>
          <p className="hd-meta">Sign in to your venue.</p>
        </div>

        <form className="hd-stack" onSubmit={onSubmit}>
          <div className="hd-field">
            <label className="hd-label" htmlFor="login-email">Email</label>
            <input
              id="login-email"
              className="hd-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="hd-field">
            <label className="hd-label" htmlFor="login-password">Password</label>
            <input
              id="login-password"
              className="hd-input"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>

          <label className="hd-row" style={{ gap: 8, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
            />
            Keep me signed in for 30 days
          </label>

          {error && (
            <div className="hd-badge hd-badge-danger" role="alert">{error}</div>
          )}

          <button className="hd-btn hd-btn-primary" type="submit" disabled={busy}>
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="hd-hint">
          New here? <Link to="/signup">Create an account</Link>.
        </p>

        <p className="hd-hint">
          Running a venue? <Link to="/">Register your venue</Link>.
        </p>
      </div>
    </div>
  );
}
